import React, { useState } from 'react';
import { AIChatChevronLeftIcon } from '@/assets/icons/icons';
import type { AIChatThinkingStep } from '../types';

// A `thought` frame has no tool name (and no pending phase -- it arrives as
// one complete chunk), so it's tracked with this sentinel name rather than
// adding a separate `kind` field to AIChatThinkingStep.
export const THOUGHT_STEP_NAME = '__thought__';

// Backend tool function names -> short user-facing labels. A name missing
// from this map (e.g. a new tool added on the backend before this map is
// updated) still renders reasonably via the fallback below rather than
// showing nothing or a raw function name.
const TOOL_STEP_LABELS: Record<string, string> = {
  query_shipments: 'Searching shipments',
  get_shipment_status_breakdown: 'Checking shipment status',
  get_shipment_status_history: 'Checking shipment history',
  get_shipment_chain_of_custody: 'Checking chain of custody',
  get_shipment_products: 'Checking shipment products',
  get_shipment_alerts: 'Checking shipment alerts',
  search_product_manual: 'Searching product manuals',
  [THOUGHT_STEP_NAME]: 'Thought',
};

function labelForStep(name: string): string {
  const known = TOOL_STEP_LABELS[name];
  if (known) return known;
  const spaced = name.replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : name;
}

// Past-tense labels, used once a step's own status is 'completed' --
// matching Ramp's reference ("Searched shipments" rather than "Searching
// shipments" once done). Used by the settled list unconditionally, and by
// the header for anything except the live-header overrides below. A name
// missing here falls back to the present-tense label unchanged.
const PAST_TENSE_STEP_LABELS: Record<string, string> = {
  query_shipments: 'Searched shipments',
  get_shipment_status_breakdown: 'Checked shipment status',
  get_shipment_status_history: 'Checked shipment history',
  get_shipment_chain_of_custody: 'Checked chain of custody',
  get_shipment_products: 'Checked shipment products',
  get_shipment_alerts: 'Checked shipment alerts',
  search_product_manual: 'Searched product manuals',
};

function displayLabelForStep(step: AIChatThinkingStep): string {
  if (step.status !== 'completed') return labelForStep(step.name);
  return PAST_TENSE_STEP_LABELS[step.name] ?? labelForStep(step.name);
}

// Live-header-only exception: a thought has no pending phase (it's always
// immediately 'completed', see THOUGHT_STEP_NAME) so it never gets to earn
// a present-tense reading from displayLabelForStep's status check the way a
// still-running tool call does. While it's the most recent thing and the
// turn is still active, the header shows this instead -- the settled list
// still always reads "Thought" (past tense), matching everything else once
// a turn is done.
const LIVE_HEADER_OVERRIDES: Record<string, string> = {
  [THOUGHT_STEP_NAME]: 'Thinking',
};

function headerLabelForStep(step: AIChatThinkingStep): string {
  return LIVE_HEADER_OVERRIDES[step.name] ?? displayLabelForStep(step);
}

type AIChatThinkingTimelineProps = {
  steps: AIChatThinkingStep[];
  done: boolean;
};

// Variant 1 of the thinking-state display: a collapsible timeline. Reads
// only `steps`/`done` -- swapping in a different display style later (e.g.
// an always-collapsed "just show the latest step" variant) means writing a
// new small component against this same shape, not touching how `steps` is
// built.
const AIChatThinkingTimeline: React.FC<AIChatThinkingTimelineProps> = ({
  steps,
  done,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasSteps = steps.length > 0;
  // A finished turn with zero steps has nothing worth summarizing -- this
  // is the only case with truly nothing to render (the live "nothing yet"
  // state below still shows the spinner + generic text).
  if (done && !hasSteps) return null;

  const latest = hasSteps ? steps[steps.length - 1] : null;
  // Expanded, the list right below is already showing the precise step (see
  // the last row's isCurrent handling), so the header becomes a generic
  // heading instead of repeating that detail -- collapsing it goes right
  // back to showing the precise current step, not "Working".
  const headerText = done
    ? 'Work completed'
    : expanded
      ? 'Working'
      : latest
        ? headerLabelForStep(latest)
        : 'Thinking';

  return (
    <div
      className={`ai-chat-drawer__thinking-timeline${
        done ? ' ai-chat-drawer__thinking-timeline--done' : ''
      }`}
    >
      <button
        type='button'
        className='ai-chat-drawer__thinking-timeline-header'
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        {!done && (
          <span
            className='ai-chat-drawer__thinking-timeline-spinner'
            aria-hidden='true'
          >
            <span className='ai-chat-drawer__thinking-timeline-orb' />
            <span className='ai-chat-drawer__thinking-timeline-orb' />
            <span className='ai-chat-drawer__thinking-timeline-orb' />
            <span className='ai-chat-drawer__thinking-timeline-orb' />
          </span>
        )}
        {/* Keyed on the text itself so changing status (not just re-renders
            with the same text) forces a remount, replaying the flip-in
            animation exactly when what it says actually changes. */}
        <span
          key={headerText}
          className='ai-chat-drawer__thinking-timeline-status'
        >
          {headerText}
        </span>
        <span
          className='ai-chat-drawer__thinking-timeline-caret'
          style={{ transform: `rotate(${expanded ? 270 : 180}deg)` }}
        >
          <AIChatChevronLeftIcon width={12} height={12} color='currentColor' />
        </span>
      </button>
      {/* Rendered even with zero steps -- the chevron is always clickable
          now, and an empty list is an honest, harmless reveal for "nothing
          has happened yet" rather than a dead control with nothing behind
          it. */}
      <div
        className={`ai-chat-drawer__thinking-timeline-list-track${
          expanded
            ? ' ai-chat-drawer__thinking-timeline-list-track--expanded'
            : ''
        }`}
      >
        <ul className='ai-chat-drawer__thinking-timeline-list'>
          {/* Placeholder row for the bootstrap window -- before any real
              step exists, the list would otherwise be empty when expanded.
              Only ever reachable while `!done` (a finished, zero-step turn
              returns null before this point), so it never needs the past-
              tense "Thought" reading -- "Thinking" is the only case. */}
          {!hasSteps && (
            <li className='ai-chat-drawer__thinking-timeline-item ai-chat-drawer__thinking-timeline-item--pending ai-chat-drawer__thinking-timeline-item--current'>
              <span className='ai-chat-drawer__thinking-timeline-dot' />
              Thinking
            </li>
          )}
          {steps.map((step, index) => {
            // The last row is the same step the header is showing -- give
            // it the header's treatment (so a live thought reads
            // "Thinking" here too, not "Thought") while it's still
            // current. Every earlier row has already been superseded by
            // something newer, so it always reads past-tense.
            const isCurrent = !done && index === steps.length - 1;
            return (
              <li
                key={`${step.name}-${index}`}
                className={`ai-chat-drawer__thinking-timeline-item ai-chat-drawer__thinking-timeline-item--${step.status}${
                  isCurrent
                    ? ' ai-chat-drawer__thinking-timeline-item--current'
                    : ''
                }`}
                // Capped rather than index * delay uncapped -- a long
                // list should still finish revealing quickly, not take
                // proportionally longer the more steps there are.
                style={{ animationDelay: `${Math.min(index, 6) * 40}ms` }}
              >
                <span className='ai-chat-drawer__thinking-timeline-dot' />
                {isCurrent
                  ? headerLabelForStep(step)
                  : displayLabelForStep(step)}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default AIChatThinkingTimeline;
